import { useCallback, useEffect, useMemo, useState } from "react";
import { IMenuOption } from "../../../types/global";
import { useTranslation } from "react-i18next";
import { useStateContext } from "../../../contexts/auth-context";

export const useCashSalesOption = (status?: string | null) => {
  const { t } = useTranslation();
  const {
    state: { permissions },
  } = useStateContext();

  const defaultOptions: IMenuOption[] = useMemo(
    () => [
      {
        value: "แก้ไข",
        disabled: false,
      },
      {
        value: "คัดลอก",
        disabled: false,
      },
      {
        value: "ยกเลิก",
        disabled: false,
      },
    ],
    []
  );

  const editOption = defaultOptions[0];
  const copyOption = defaultOptions[1];
  const cancelOption = defaultOptions[2];

  const externalRefOption: IMenuOption = useMemo(
    () => ({ value: t("sales.external_reference_no"), disabled: false }),
    [t]
  );

  const canUpdate = useCallback(() => {
    return permissions?.cash_sales?.update;
  }, [permissions]);

  const canCancel = useCallback(() => {
    return permissions?.cash_sales?.cancel;
  }, [permissions]);

  const getOptionsForStatus = useCallback(
    (status?: string | null) => {
      const isCreate = permissions?.cash_sales?.create;

      if (["draft"].includes(status || "")) {
        if (canCancel()) {
          return isCreate ? [copyOption, cancelOption] : [];
        }
        return [];
      }

      if (status === "cancelled") {
        return isCreate ? [copyOption] : [];
      }

      if (status === "finished") {
        if (canUpdate()) {
          return isCreate
            ? [externalRefOption, copyOption]
            : [externalRefOption];
        }
        return isCreate ? [copyOption] : [];
      }

      // Default case when no specific status matches
      return canUpdate() && canCancel()
        ? isCreate
          ? [externalRefOption, ...defaultOptions]
          : [editOption, cancelOption]
        : canUpdate()
        ? isCreate
          ? [copyOption, editOption]
          : [editOption]
        : canCancel()
        ? isCreate
          ? [copyOption, cancelOption]
          : [cancelOption]
        : [];
    },
    [
      permissions?.cash_sales?.create,
      canUpdate,
      canCancel,
      externalRefOption,
      defaultOptions,
      editOption,
      cancelOption,
      copyOption,
    ]
  );

  const [selectModifyOptions, setSelectedModifyOptions] =
    useState<IMenuOption[]>(defaultOptions);

  useEffect(() => {
    const options = getOptionsForStatus(status);
    setSelectedModifyOptions(options);
  }, [status, getOptionsForStatus]);

  return selectModifyOptions;
};
